<!-- eslint-disable vue/no-v-html -->
<template>
    <b-form-group label-for="searcher">
        <template v-if="!hideLabel" #label>
            {{ label }}{{ label?.length ? ":" : "" }}<span v-if="required"> *</span>
            <HelpPopup v-if="resolvedHelpHtml && !disabled">
                <div v-html="resolvedHelpHtml" />
            </HelpPopup>
        </template>
        <v-select id="searcher" ref="dropdown" v-model="object" :clearable="clearable" :disabled="disabled"
            :filterable="false" :get-option-label="printLabel" :options="formattedObjects" :select-on-tab="true"
            :class="validState + 'style-chooser ' + size + (truncate ? ' truncate-enabled' : '') + (dropup ? ' dropup' : '')" dir="ltr"
            :dropdown-open="true" :selectable="canSelect" :append-to-body="appendToBody" @search="searchValueUpdate">
            <template #option="object">
                <div v-if="object.heading" class="heading">{{ object.label }}</div>
                <HelpPopup v-else-if="useItemPopover" button-component="div" :delay="{ show: 600, hide: 100 }"
                    triggers="hover hover" placement="left" icon="none" boundary="viewport" :muted="false">
                    <template #label>
                        <div style="text-wrap: wrap" v-html="printLabel(object)"></div>
                    </template>
                    <slot name="item-popover" :object="object" />
                </HelpPopup>
                <div v-else style="text-wrap: wrap"
                    :title="printLabel(object) != printLabel(object, true) ? printLabel(object, true) : null"
                    v-html="printLabel(object)" />
            </template>
            <template #selected-option="object">
                <div style="line-height: 1.25; margin: 0; padding: 0;" v-html="printLabel(object, true)" />
            </template>
        </v-select>
    </b-form-group>
</template>

<script lang="ts">
import { Component, Emit, Prop, Vue, Watch } from "vue-property-decorator";
import { debounce } from "throttle-debounce";
import { Drug } from "@/models/Drug/Drug";
import HasLabel from "@/models/HasLabel";
import Axios, { CancelTokenSource } from "axios";
import { DrugProduct, DrugSource } from "@/models/Drug/DrugProduct";
import { NonGSDDDrug } from "@/models/Drug/NonGSDDDrug";
import { CompoundDrug } from "@/models/Drug/CompoundDrug";
import HelpPopup from "@/components/HelpPopup.vue";

@Component({
    name: "SearchComponent",
    components: { HelpPopup },
})
export default class SearchComponent extends Vue {
    /**
     * @searchURL: String that represents the URL in the API to look for the search results
     * by the introduced value in the component.
     */
    @Prop() protected searchURL!: string;
    /**
     * @getByIdURL: String that represents the URL in the API to look for a specific result
     * by a given ID in the v-model or in the @id prop.
     */
    @Prop() protected getByIdURL!: string;
    /**
     * @disabled: Optional boolean value to enable/disable the dropdown.
     */
    @Prop({
        default: false,
        type: Boolean,
    }) protected disabled!: boolean;
    /**
     * @clearable: Optional boolean value to show or hide the clear selection button in the dropdown.
     */
    @Prop({ default: false, type: Boolean, }) protected clearable!: boolean;
    /**
     * @id: Optional numeric value which should be the unique identifier for a given entity
     * which we want to showup as selected in the dropdown
     */
    @Prop({ default: 0 }) protected id!: number | string;
    /**
     * @optionLabel: Funcion callback that defines the way the elements in the dropdown are going to be displayed.
     */
    @Prop() protected optionLabel!: Function;
    /**
     * label: Optional string label text to display at the top of the component.
     */
    @Prop({ default: `Search` }) protected label: string | undefined;
    /**
     * params: Optional custom object with arguments we need to send to the searchURL API endpoint.
     */
    @Prop() private params!: any;
    /**
     * params: Optional custom object with arguments we need to send to the getById URL API endpoint.
     * The getById URL doesn't need to be set to be able to use these params.
     */
    @Prop({
        default: () => {
            return {};
        },
    })
    protected paramsInGetById!: any;
    /**
     * @params: Optional Function callback that is called right after the results are being received
     * from the SearchURL API endpoint, it sends the results back to the function to be modified
     * or perform any other operation needed before being shown in the dropdown component.
     */
    @Prop({ default: null }) private resultsModifier!: Function;
    /**
     * @prepopulate : Boolean value, set to true to populate the dropdown by default with
     * the results returned by the API receiving as arguments an empty string
     * and params in request if there is any.
     */
    @Prop({
        type: Boolean,
        default: false,
    }) private prepopulate!: boolean;
    @Prop({
        type: [Boolean, Function],
        default: false,
    }) private autoselect!: boolean | Function;
    @Prop({
        type: Boolean,
        default: false,
    }) protected createNewObject!: boolean;
    @Prop({
        type: String,
        default: "",
    }) protected createNewLabel!: string;
    @Prop() private value!: any;
    @Prop({
        default: false,
        type: Boolean,
    }) protected required!: boolean;
    @Prop({
        default: false,
        type: Boolean,
    }) private shouldFocus!: boolean;
    @Prop({ default: false }) protected hideLabel!: boolean;
    @Prop({ default: null }) protected helpHtml!: string | null;
    @Prop({ default: "" }) protected size!: string;
    @Prop({ default: null }) protected state?: boolean | null;
    @Prop({ default: false }) protected useItemPopover!: boolean;
    @Prop({ default: false }) protected truncate!: boolean;
    @Prop({ default: false, type: Boolean }) protected appendToBody!: boolean;
    /**
     * @dropup: Optional boolean to position dropdown above the input field instead of below
     */
    @Prop({ default: false, type: Boolean }) protected dropup!: boolean;

    /**
     * @initialSearchValue: Optional string to use as starting search value.
     * Expected to be used in conjunction with @prepopulate = true
     */
    @Prop({ default: "" }) protected initialSearchValue!: string;

    /**
     * @staticOptions: Optional array of options shown when the input is empty.
     * When provided, the dropdown displays these locally without making an API call,
     * letting parents surface a context-specific list (e.g., a patient's previous
     * prescribers) on focus. The result modifier still runs and receives an empty
     * searchTerm. As the user types, behavior reverts to the normal API-backed search.
     */
    @Prop({ default: () => [] }) private staticOptions!: any[];

    protected objects: any[] = [];
    private modelObject: any = {};
    private debouncedSearch: any = debounce(500, (searchValue: string, loading: any) => {
        this.onSearch(searchValue, loading);
    });
    private searching = false;
    private isInitial = true;

    get object(): any {
        return this.toModelType(this.value);
    }

    set object(val: any) {
        // The best practice is to use @input event in parent components instead of watchers.
        this.$emit("input", val);
    }

    created() {
        if ((this.value === null || this.value === undefined) && !this.optionLabel) {
            console.error("SearchComponent: v-model is null or undefined");
        }

        if (!this.optionLabel && !(this.value instanceof HasLabel)) {
            console.error("SearchComponent: Not receiving any optionLabel and the model is not a HasLabel object.");
        }

        if (this.value instanceof HasLabel) {
            this.modelObject = new this.value.constructor(this.modelObject);
        }

        if (this?.value?.id) this.reloadObject(this.value, {});

        // Pre-populate from staticOptions before the first render so the dropdown
        // never paints with an empty list when the parent already has the data
        // (e.g., a layout toggle that re-mounts this component while
        // patientPrescribers is already cached). Doing this in created() — not
        // mounted() — avoids the post-mount nextTick window where the user can
        // open the dropdown before Vue flushes the reactive update.
        if (this.staticOptions.length) {
            this.onSearch("");
        }

        this.enableNewObject();
    }

    // noinspection JSUnusedGlobalSymbols
    mounted() {
        const dropdown: any = this.$refs.dropdown;
        if (this.prepopulate || this.staticOptions.length) {
            if (dropdown?.toggleLoading) this.searchValueUpdate(this.initialSearchValue, dropdown.toggleLoading);
        }
        if (this.shouldFocus) {
            this.$el.querySelector(dropdown.searchInputQuerySelector).focus();
        }
    }

    @Watch("staticOptions")
    onStaticOptionsChange() {
        // Static options often load asynchronously (e.g., a patient's recent
        // prescribers) — refresh the empty-state display when they arrive.
        // Skip only if the user is actively typing a search; the v-select
        // dropdown list still renders from `objects` even when a value is
        // already selected, so leaving it empty would show "no matching options".
        const dropdown: any = this.$refs.dropdown;
        if (dropdown?.search) return;
        this.searchValueUpdate("", dropdown?.toggleLoading);
    }

    @Watch("searchURL")
    searchUrlChanged() {
        const dropdown: any = this.$refs.dropdown;
        if (dropdown?.toggleLoading) this.searchValueUpdate(this.initialSearchValue, dropdown.toggleLoading);

    }

    printLabel(o: any, long: boolean = false) {
        if (o.id == 0 && this.createNewObject) return this.createNewLabel;
        o = this.toModelType(o);
        if (this.optionLabel) {
            return this.optionLabel(o);
        } else if (o instanceof HasLabel) {
            const model = this.toModelType(o);
            const label = model.getLabel(long);
            const suffix = model.getLabelSuffixHtml();
            return suffix ? `${label} ${suffix}` : label;
        } else {
            return o ? o.id : "";
        }
    }

    get resolvedHelpHtml() {
        // An explicit helpHtml prop wins; otherwise derive it from the selected object
        // (Patient/Prescriber implement getSearchHelpHtml, most models don't).
        if (this.helpHtml) return this.helpHtml;
        if (!this.object) return null;
        if ("getSearchHelpHtml" in this.object) {
            return this.object.getSearchHelpHtml();
        }
        return null;
    }

    searchValueUpdate(value: string, loading: any) {
        value = value || "";

        // Empty-input + staticOptions is a local-only render — running it through
        // the debouncer would leave `objects` empty for 500ms, which is visible
        // when the dropdown opens immediately after mount (e.g., layout toggles
        // that re-mount this component while patientPrescribers is already cached).
        // Note: we don't cancel the debouncer here — throttle-debounce v2's
        // .cancel() permanently disables the function. Stale invocations are
        // dropped by the live-value check at the top of onSearch instead.
        if (value === "" && this.staticOptions.length) {
            this.onSearch(value, loading);
            return;
        }

        if (value || (value == "" && this.prepopulate)) {
            this.debouncedSearch(value, loading);
        }
    }

    reloadResults() {
        const dropdown: any = this.$refs.dropdown;
        if (dropdown?.toggleLoading) this.searchValueUpdate(this.initialSearchValue, dropdown.toggleLoading);
    }

    private cancelTokenSource: CancelTokenSource | null = null;

    /**
     * Monotonic counter used by forceReload as a latest-wins guard. Unlike
     * onSearch, forceReload has no request cancellation, so a stale GET /{id}
     * response that resolves after a newer selection would clobber the current
     * value. Each forceReload increments this and only applies its response if
     * it is still the most recent request.
     */
    protected reloadSeq = 0;

    onSearch(value: string, loading?: any) {
        // Stale-debouncer guard: when a non-empty `value` was queued via
        // debouncedSearch and the user then cleared the input (routing to the
        // sync staticOptions path), the original timer still fires later with
        // the old typed value. If the live input no longer matches, drop it so
        // we don't clobber the static list with stale API results.
        // Exempt the initial prepopulate search (isInitial still true): it is
        // fired programmatically from initialSearchValue while the input box is
        // empty, so it would never match the live input and must not be dropped.
        if (value !== "" && !this.isInitial) {
            const currentSearch = (this.$refs.dropdown as any)?.search ?? "";
            if (currentSearch !== value) return;
        }

        if (this.searching) {
            this.cancelTokenSource?.cancel("New search started");
            this.searching = false;
        }

        // Empty input + staticOptions: render the local list without an API call.
        // Parents use this to surface a context-specific set (e.g., a patient's
        // recent prescribers) on focus without paying for a global query.
        if (value === "" && this.staticOptions.length) {
            const params = { ...(this.params ?? {}), searchTerm: value };
            const data = this.staticOptions;
            this.objects = this.resultsModifier ? this.resultsModifier(data, params) : data;
            this.objects = this.objects.map((o) => this.toModelType(o));
            this.isInitial = false;
            this.enableNewObject();
            return;
        }

        if (
            (value.length && this.searchURL.length && !this.searching) ||
            (value == "" && this.prepopulate)
        ) {
            if (loading) loading(true);

            const paramsInRequest = this.params || { searchTerm: value };
            paramsInRequest.searchTerm = value;

            this.cancelTokenSource = Axios.CancelToken.source();
            this.searching = true;
            Axios.get(this.searchURL, {
                params: paramsInRequest,
                cancelToken: this.cancelTokenSource.token,
            })
                .then((response) => {
                    if (response.status == 204) response.data = [];
                    this.objects = response.data;

                    if (this.resultsModifier) {
                        this.objects = this.resultsModifier(response.data, paramsInRequest);
                    } else {
                        this.objects = response.data;
                    }

                    // this.enableNewObject();

                    this.objects = this.objects.map((o) => this.toModelType(o));

                    if (this.isInitial && this.autoselect) {
                        if (typeof this.autoselect === "function") {
                            const obj = this.autoselect(this.objects);
                            if (obj) this.object = obj;
                        } else if (this.objects.length === 1) {
                            this.object = this.objects[0];
                        } else if (this.objects.length > 1) {
                            const dropdown: any = this.$refs.dropdown;
                            console.log("attempt focus on dropdown: ", dropdown);
                            dropdown.focus();
                        }
                    }
                })
                .catch((error) => {
                    if (Axios.isCancel(error)) {
                        console.log(error);
                        return;
                    }
                    console.error(error, { response: error?.response });
                })
                .finally(() => {
                    if (loading) loading(false);
                    this.searching = false;
                    this.isInitial = false;
                    this.enableNewObject();
                });
        }
    }

    @Watch('autoselect')
    onAutoselectChange() {
        this.isInitial = true;
        this.onSearch("");
    }

    enableNewObject() {
        if (this.createNewObject && !this.isInitial && !this.objects.some(obj => obj.id === 0)) {
            this.objects.push({ id: 0 });
        }
    }

    @Emit('results')
    @Watch('objects')
    onObjectsChange(value: any[], _oldValue: any[]) {
        return value;
    }

    // Removed to prevent triggering watchers twice.
    // @Watch('object') onObjectChange(value: any, oldValue: any) {
    //     this.reloadObject(value, oldValue);
    // }

    @Watch("id") onIdChange(id: number, oldId: number) {
        this.reloadObject({ id }, { id: oldId });
    }

    reloadObject(value: any, oldValue: any) {
        if (!value || !value?.id) return;
        if (oldValue && (oldValue?.id || 0) == (value?.id || 0)) return;
        this.forceReload(value);
    }

    forceReload(value: any) {
        const url = this.getByIdURL || this.searchURL;

        const dropdown: any = this.$refs.dropdown;
        const toggleLoading = dropdown?.toggleLoading;

        // Latest-wins guard: ignore a response if a newer reload has started
        // since, so a stale GET /{id} can't overwrite the current selection.
        const seq = ++this.reloadSeq;

        if (toggleLoading) toggleLoading(true);
        Axios.get(`${url}/${value.id}`, { params: this.paramsInGetById })
            .then((response) => {
                if (seq !== this.reloadSeq) return;
                this.object = this.toModelType(response.data);
            })
            .catch((error) => {
                console.warn(error);
            })
            .finally(() => {
                if (seq === this.reloadSeq && toggleLoading) toggleLoading(false);
            });
    }

    @Watch("object")
    onChange() {
        if (this.createNewObject && this.object.id == 0) {
            this.$emit("createNewObject");
        }
    }

    toModelType(o: any) {
        if (o === null || o === undefined) {
            if (this.modelObject instanceof HasLabel) {
                const Constructor = this.modelObject.constructor as any;
                return new Constructor();
            }
            return o;
        }

        if (this.modelObject instanceof DrugProduct) {
            const drugObj = o as DrugProduct;
            switch (drugObj.source) {
                case DrugSource.GsddFederal:
                    return new Drug(drugObj.drugId, drugObj);
                case DrugSource.NonGsddProduct:
                    return new NonGSDDDrug(drugObj.drugId, drugObj);
                case DrugSource.CompoundProduct:
                    return new CompoundDrug(drugObj.drugId, drugObj);
            }
        }

        if (this.modelObject instanceof HasLabel) {
            const Constructor = this.modelObject.constructor as any;
            return new Constructor(o);
        }

        return o;
    }

    canSelect(o: any) {
        return o.heading !== true;
    }

    public get formattedObjects() {
        if (this.object && "formatSearchOptions" in this.object) {
            return this.object.formatSearchOptions(this.objects, this);
        }
        return this.objects;
    }

    public focus() {
        (this.$refs.dropdown as any).$refs.search.focus();
    }

    get validState() {
        //console.log("validState: ", this.state);
        if (this.state === false) return 'is-invalid ';
        if (this.state === true) return 'is-valid ';
        if (this.state === null) return '';
    }
}
</script>

<style scoped lang="scss">
::v-deep(li:has(.heading)) {
    font-weight: bolder;
    font-size: .8em;
    text-transform: uppercase;
    border-bottom: 1px solid rgb(221, 221, 221);
    background: rgb(238, 238, 238);
    color: rgb(100, 100, 100);
    cursor: default;
    margin-top: 6px;
    position: sticky;
    top: -5px;
    letter-spacing: .5px;
    box-shadow: 0 0 3px rgb(221, 221, 221);
}

::v-deep(li:has(.heading):first-child) {
    margin-top: -5px;
}

::v-deep(li:has(.heading):hover) {
    background: rgb(238, 238, 238);
}

.style-chooser.sm {
    font-size: .95em;

    ::v-deep(.vs__dropdown-toggle) {
        height: 32px;
    }
}

/* Truncation styles for vue-select when truncate is enabled */
.truncate-enabled {
    ::v-deep(.vs__dropdown-toggle) {
        overflow: hidden;
    }

    ::v-deep(.vs__selected-options) {
        overflow: hidden;
        flex: 1;
        min-width: 0;
        max-width: 100%;
    }

    ::v-deep(.vs__selected) {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 100%;
        flex-shrink: 1;
        min-width: 0;
    }
}

.is-invalid {
    ::v-deep(.vs__dropdown-toggle) {
        border: 1px solid #dc3545;
    }

    ::v-deep(.vs__search) {
        background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='none' stroke='%23dc3545' viewBox='0 0 12 12'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23dc3545' stroke='none'/%3e%3c/svg%3e");
        background-repeat: no-repeat;
        background-position: right calc(0.375em + 0.1875rem) center;
        background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
    }
}

.is-valid {
    ::v-deep(.vs__dropdown-toggle) {
        border-color: #28a745;
        padding-right: calc(1.5em + 0.75rem) !important;
        background-image: url("data:image/svg+xml,%3csvg xmlns= 'http://www.w3.org/2000/svg' width= '8' height= '8' viewBox= '0 0 8 8' %3e%3cpath fill= '%2328a745' d= 'M2.3 6.73L.6 4.53c-.4-1.04.46-1.4 1.1-.8l1.1 1.4 3.4-3.8c.6-.63 1.6-.27 1.2.7l-4 4.6c-.43.5-.8.4-1.1.1z' /%3e%3c/svg%3e");
        background-repeat: no-repeat;
        background-position: right calc(0.375em + 0.1875rem) center;
        background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
    }
}

::v-deep(.vs__dropdown-option--highlight .hasLabel) {
    color: white !important;
}

::v-deep(.vs__selected-options) {
    flex-wrap: nowrap !important;
}

.dropup ::v-deep(.vs__dropdown-menu) {
    bottom: 100% !important;
    top: auto !important;
    margin-bottom: 5px;
}
</style>
